import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { requestMagicLink } from '../api.js';
import { signInErrorMessage } from '../errors.js';
import { AuthLayout } from '../components/AuthLayout.js';
import { Brand } from '../components/Brand.js';
import { safeReturnPath } from '../return-path.js';

// The demo workspace. The magic-link flow is tenant-scoped, so the form submits a tenant slug;
// prefilled for the single seeded tenant so the happy path is one field (email) for now.
const DEFAULT_TENANT_SLUG = 'ggp';

export function SignIn() {
  const [params] = useSearchParams();
  const linkError = signInErrorMessage(params.get('error'));
  // WHERE THEY WERE TRYING TO GO before the session lapsed (#968), put here by `signInPathFor`.
  // Filtered on the way IN as well as on the way out: this value came from a URL, so it is
  // attacker-supplied, and it is about to be handed to the api to be signed into a token. A
  // candidate that does not survive is simply dropped — sign-in still works and lands at `/`.
  const returnPath = safeReturnPath(params.get('next'), window.location.origin);

  const [email, setEmail] = useState('');
  const [tenantSlug, setTenantSlug] = useState(DEFAULT_TENANT_SLUG);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Always resolves (api is always 202 — no enumeration). Show the same confirmation either way.
      await requestMagicLink(
        email.trim().toLowerCase(),
        tenantSlug.trim().toLowerCase(),
        returnPath,
      );
      setSent(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout>
      <Brand />
      {sent ? (
        <>
          <h1>Check your email</h1>
          <p className="sub">
            If <strong>{email}</strong> is a member of this workspace, a one-time sign-in link is on
            its way. The link expires shortly.
          </p>
          <button type="button" className="link-btn" onClick={() => setSent(false)}>
            Use a different email
          </button>
        </>
      ) : (
        <>
          <h1>Sign in to GGPathOS Deal Analyzer</h1>
          <p className="sub">We&rsquo;ll email you a secure, passwordless sign-in link.</p>
          {linkError && <div className="notice error">{linkError}</div>}
          <form onSubmit={(e) => void onSubmit(e)}>
            <label htmlFor="email">Work email</label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <label htmlFor="tenant">Workspace</label>
            <input
              id="tenant"
              type="text"
              required
              placeholder="workspace"
              value={tenantSlug}
              onChange={(e) => setTenantSlug(e.target.value)}
            />
            <button type="submit" disabled={submitting}>
              {submitting ? 'Sending…' : 'Email me a sign-in link'}
            </button>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
